import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore, Contact } from '../stores/userStore';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Send, Users, AtSign, HelpCircle, Sparkles, MessageCircle, Menu, X, Plus, Clock, Trash2, ChevronLeft } from 'lucide-react';
import StarAnimation from '../components/StarAnimation';
import { chatPersistenceService, ChatMessage } from '../services/chatPersistenceService';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface GroupChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  taggedContacts?: Contact[];
  query?: string;
  isLoading?: boolean;
  isExpanded?: boolean;
}

interface Conversation {
  conversationId: string;
  lastMessageTime: Date | string;
  messageCount: number;
  title: string;
  preview?: string;
}

const GroupCompatibilityChat: React.FC = () => {
  const { user: authUser, userStatus } = useAuth();
  const { user: storeUser, addContact, importPendingContactCharts, retryContactChartImport, syncWithAuth, updateUser } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Use storeUser if available, otherwise use authUser
  const user = storeUser || authUser;
  const contacts = storeUser?.contacts || [];
  
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'chat' | 'history'>('chat');
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isRestoringConversation, setIsRestoringConversation] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [hasSeenWelcomeMessage, setHasSeenWelcomeMessage] = useState(false);
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);
  const [lastMessageClearTime, setLastMessageClearTime] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const previousMessageCountRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll to bottom only for truly new messages
  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousMessageCount = previousMessageCountRef.current;
    
    // Only auto-scroll if:
    // 1. We're not restoring a conversation
    // 2. We're not loading a conversation
    // 3. We actually added new messages (not just loaded existing ones)
    // 4. We have messages to scroll to
    if (!isRestoringConversation && 
        !isLoadingConversation &&
        currentMessageCount > previousMessageCount && 
        currentMessageCount > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Update the previous count for next comparison
    previousMessageCountRef.current = currentMessageCount;
  }, [messages, isRestoringConversation, isLoadingConversation]);

  // Auto-import pending contact charts when component loads
  useEffect(() => {
    if (user?.uid && contacts.length > 0) {
      const pendingContacts = contacts.filter(c => c.chartStatus === 'pending');
      if (pendingContacts.length > 0) {
        importPendingContactCharts().catch(error => {
          console.error('Error auto-importing pending contacts:', error);
        });
      }
    }
  }, [user?.uid, contacts.length]);

  // Save scroll position when scrolling (debounced to prevent loops)
  useEffect(() => {
    const handleScroll = () => {
      // Don't track scroll position if we're restoring it
      if (isRestoringScroll) return;
      
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll position updates
      scrollTimeoutRef.current = setTimeout(() => {
        const container = document.querySelector('.chat-messages-container');
        if (container) {
          setScrollPosition(container.scrollTop);
        }
      }, 100);
    };

    const container = document.querySelector('.chat-messages-container');
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [isRestoringScroll]);

  // Restore scroll position after conversation is loaded
  useEffect(() => {
    if (!isRestoringConversation && !isLoadingConversation && scrollPosition > 0) {
      const container = document.querySelector('.chat-messages-container');
      if (container) {
        setIsRestoringScroll(true);
        container.scrollTop = scrollPosition;
        // Reset the flag after a short delay
        setTimeout(() => setIsRestoringScroll(false), 100);
      }
    }
  }, [isRestoringConversation, isLoadingConversation, scrollPosition]);

  // Update URL when conversation changes
  useEffect(() => {
    if (user?.uid) {
      if (currentConversationId) {
        setSearchParams({ conversation: currentConversationId, view: viewMode });
      } else {
        setSearchParams({ view: viewMode });
      }
    }
  }, [user?.uid, searchParams, setSearchParams]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (user?.uid) {
      const stateToSave = {
        conversationId: currentConversationId,
        viewMode,
        scrollPosition,
        timestamp: Date.now()
      };
      localStorage.setItem(`groupChatState_${user.uid}`, JSON.stringify(stateToSave));
    }
  }, [currentConversationId, viewMode, scrollPosition, user?.uid]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlConversationId = searchParams.get('conversation');
      const urlViewMode = searchParams.get('view') as 'chat' | 'history' || 'chat';
      
      setCurrentConversationId(urlConversationId);
      setViewMode(urlViewMode);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [searchParams]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Reset isStartingNewChat flag after it's been processed
  useEffect(() => {
    if (isStartingNewChat) {
      // Reset the flag after a short delay to allow the new chat setup to complete
      const timer = setTimeout(() => {
        setIsStartingNewChat(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isStartingNewChat]);

  // Add welcome message on first load
  useEffect(() => {
    // Only skip if we're explicitly starting a new chat
    if (isStartingNewChat) {
      return;
    }
    
    // Don't skip on page refresh - we need to load existing conversations
    const isPageRefresh = !isStartingNewChat && messages.length === 0 && !currentConversationId;
    
    const loadChatHistory = async () => {
      if (user?.uid) {
        try {
          if (currentConversationId) {
            // Load specific conversation
            setIsLoadingConversation(true);
            setIsRestoringConversation(true);
            const dbMessages = await chatPersistenceService.loadChatHistory(50, currentConversationId);
            if (dbMessages.length > 0) {
              setMessages(dbMessages);
              setIsLoadingConversation(false);
              return;
            }
          } else {
            // Load recent chat history or most recent conversation
            // First try to get recent conversations to find the most recent one
            const recentConversations = await chatPersistenceService.getRecentConversations(5);
            if (recentConversations && recentConversations.length > 0) {
              // Get the most recent conversation
              const mostRecentConversation = recentConversations[0];
              
              // Set the conversation ID
              setCurrentConversationId(mostRecentConversation.id);
              
              // Load messages for this conversation
              const dbMessages = await chatPersistenceService.loadChatHistory(50, mostRecentConversation.id);
              if (dbMessages.length > 0) {
                setMessages(dbMessages);
                // Update URL to reflect the loaded conversation
                setSearchParams({ conversation: mostRecentConversation.id, view: 'chat' });
                return;
              }
            } else {
              // Try to load any recent messages without conversation ID
              const dbMessages = await chatPersistenceService.loadChatHistory(50);
              if (dbMessages.length > 0) {
                setMessages(dbMessages);
                return;
              }
            }
          }
          
          // If no database messages, show welcome message
          setIsRestoringConversation(false);
          setIsLoadingConversation(false);
          showWelcomeMessage();
        } catch (error) {
          console.error('Error loading chat history:', error);
          setIsRestoringConversation(false);
          setIsLoadingConversation(false);
          showWelcomeMessage();
        }
      }
    };

    loadChatHistory();
  }, [user?.uid, user?.birthData, currentConversationId, isStartingNewChat, messages.length, lastMessageClearTime]);

  // Separate function for loading conversation by ID (can be called from other functions)
  const loadConversationById = async (conversationId: string) => {
    if (user?.uid) {
      try {
        setIsLoadingConversation(true);
        setIsRestoringConversation(true);
        const dbMessages = await chatPersistenceService.loadChatHistory(50, conversationId);
        if (dbMessages.length > 0) {
          setMessages(dbMessages);
          setIsLoadingConversation(false);
          setIsRestoringConversation(false);
          return;
        }
        
        // If no messages found, show welcome message
        setIsRestoringConversation(false);
        setIsLoadingConversation(false);
        showWelcomeMessage();
      } catch (error) {
        console.error('Error loading conversation by ID:', error);
        setIsRestoringConversation(false);
        setIsLoadingConversation(false);
        showWelcomeMessage();
      }
    }
  };

  const loadConversations = async () => {
    if (!user?.uid) return;
    
    setIsLoadingConversations(true);
    try {
      const recentConversations = await chatPersistenceService.getRecentConversations(20);
      // Ensure we always set an array, even if the service returns unexpected data
      const conversationsArray = Array.isArray(recentConversations) ? recentConversations : [];
      setConversations(conversationsArray);
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const showWelcomeMessage = () => {
    // Don't show welcome message if we've already seen it and we're not starting a new chat
    if (hasSeenWelcomeMessage && !isStartingNewChat) {
      return;
    }
    
    let welcomeContent = '';
    
    if (!user) {
      welcomeContent = '👋 Welcome to Group Compatibility Chat!\n\nPlease log in to start analyzing group compatibility.';
    } else if (!user.birthData || !isCompleteBirthData(user.birthData)) {
      welcomeContent = `⚠️ **Profile Setup Required**

To use group compatibility analysis, please complete your birth details in your profile:

• Birth date (day, month, year)
• Birth time (hour, minute) 
• Birth location (latitude, longitude)

**Go to Profile → Edit Profile** to add this information.

**Current Status**: ${user.birthData ? '❌ Incomplete' : '❌ Missing'}`;
    } else if (!userStatus || !userStatus.astroAgentsLive || userStatus.astroAgentsLive === 0) {
      welcomeContent = `📊 **Charts Import Required**

Your birth details are complete! ✅ Now please import your astrological charts:

• Go to **Astrology** page
• Click **"Import Charts"** 
• Wait for charts to be generated

**Current Status**: Charts not imported yet

Once charts are ready, you can start analyzing group compatibility!`;
    } else {
      // Check contact status
      const contactsWithBirthData = contacts.filter(contact => 
        contact.birthData && isCompleteBirthData(contact.birthData)
      );
      
      if (contactsWithBirthData.length === 0) {
        welcomeContent = `⚠️ **Contacts Setup Required**

Your profile and charts are complete! ✅ But you need contacts with birth details:

• Go to **Profile** → **Contacts**
• Add contacts with their birth information
• Or import existing contacts

**Current Status**: ${contacts.length} contact${contacts.length !== 1 ? 's' : ''} available, but none have birth data`;
      } else {
        welcomeContent = `🌟 **Ready for Group Compatibility Analysis!** 🌟

Your profile and charts are complete! ✅
You have ${contactsWithBirthData.length} contact${contactsWithBirthData.length !== 1 ? 's' : ''} with birth data.

**What you can do:**
• Ask about compatibility between you and specific contacts
• Get insights about group dynamics
• Analyze relationship patterns
• Understand compatibility challenges and strengths

**Try asking:**
• "How compatible am I with [contact name]?"
• "What are the strengths of our relationship?"
• "What challenges might we face?"
• "How can we improve our compatibility?"`;
      }
    }

    const welcomeMessage: GroupChatMessage = {
      id: 'welcome',
      type: 'system',
      content: welcomeContent,
      timestamp: new Date()
    };

    setMessages([welcomeMessage]);
    setHasSeenWelcomeMessage(true);
  };

  const handleNewChat = () => {
    setIsStartingNewChat(true);
    setMessages([]);
    setCurrentConversationId(null);
    setViewMode('chat');
    setSearchParams({ view: 'chat' });
    setHasSeenWelcomeMessage(false);
    setLastMessageClearTime(Date.now());
    
    // Show welcome message for new chat
    setTimeout(() => {
      showWelcomeMessage();
    }, 100);
  };

  const handleClearChat = () => {
    if (currentConversationId) {
      // Delete the current conversation
      handleDeleteConversation(currentConversationId);
    } else {
      // Just clear messages locally
      setMessages([]);
      setHasSeenWelcomeMessage(false);
      setLastMessageClearTime(Date.now());
      showWelcomeMessage();
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      // Delete conversation from database
      await chatPersistenceService.deleteConversation(conversationId);
      
      // Remove from local state
      setConversations(prev => prev.filter(c => c.conversationId !== conversationId));
      
      // If this was the current conversation, start a new one
      if (currentConversationId === conversationId) {
        handleNewChat();
      }
      
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handleConversationSelect = async (conversationId: string) => {
    setCurrentConversationId(conversationId);
    setViewMode('chat');
    setSearchParams({ conversation: conversationId, view: 'chat' });
    await loadConversationById(conversationId);
  };

  const formatTime = (date: Date | string) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Unknown time';
    }
    
    const now = new Date();
    const diffInHours = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return dateObj.toLocaleDateString();
    }
  };

  // Handle @ mentions
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputMessage(value);
    
    // Check for @ mentions
    const lastAtSymbol = value.lastIndexOf('@');
    if (lastAtSymbol !== -1) {
      const afterAt = value.slice(lastAtSymbol + 1);
      const beforeSpace = afterAt.split(' ')[0];
      
      if (beforeSpace.length > 0) {
        const matchingContacts = contacts.filter(contact => 
          contact.name.toLowerCase().includes(beforeSpace.toLowerCase())
        );
        
        if (matchingContacts.length > 0) {
          setShowContactSuggestions(true);
          setSuggestionIndex(0);
        } else {
          setShowContactSuggestions(false);
        }
      } else {
        setShowContactSuggestions(false);
      }
    } else {
      setShowContactSuggestions(false);
    }
  };

  const insertContactMention = (contact: Contact) => {
    const lastAtSymbol = inputMessage.lastIndexOf('@');
    const beforeAt = inputMessage.slice(0, lastAtSymbol);
    const afterAt = inputMessage.slice(lastAtSymbol + 1);
    const afterSpace = afterAt.indexOf(' ') !== -1 ? afterAt.slice(afterAt.indexOf(' ')) : '';
    
    const newMessage = beforeAt + '@' + contact.name + afterSpace;
    setInputMessage(newMessage);
    setShowContactSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    } else if (e.key === 'Tab' && showContactSuggestions) {
      e.preventDefault();
      const matchingContacts = contacts.filter(contact => 
        contact.name.toLowerCase().includes(inputMessage.slice(inputMessage.lastIndexOf('@') + 1).split(' ')[0].toLowerCase())
      );
      if (matchingContacts.length > 0) {
        insertContactMention(matchingContacts[suggestionIndex]);
      }
    } else if (e.key === 'ArrowDown' && showContactSuggestions) {
      e.preventDefault();
      const matchingContacts = contacts.filter(contact => 
        contact.name.toLowerCase().includes(inputMessage.slice(inputMessage.lastIndexOf('@') + 1).split(' ')[0].toLowerCase())
      );
      setSuggestionIndex(prev => (prev + 1) % matchingContacts.length);
    } else if (e.key === 'ArrowUp' && showContactSuggestions) {
      e.preventDefault();
      const matchingContacts = contacts.filter(contact => 
        contact.name.toLowerCase().includes(inputMessage.slice(inputMessage.lastIndexOf('@') + 1).split(' ')[0].toLowerCase())
      );
      setSuggestionIndex(prev => (prev - 1 + matchingContacts.length) % matchingContacts.length);
    }
  };

  const extractTaggedContacts = (message: string): Contact[] => {
    const taggedContacts: Contact[] = [];
    const regex = /@(\w+)/g;
    let match;
    
    while ((match = regex.exec(message)) !== null) {
      const contactName = match[1];
      const contact = contacts.find(c => 
        c.name.toLowerCase() === contactName.toLowerCase()
      );
      if (contact) {
        taggedContacts.push(contact);
      }
    }
    
    return taggedContacts;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    if (!user) {
      toast.error('Please log in to use group compatibility chat');
      return;
    }

    // Check if user has complete birth data
    if (!user.birthData || !isCompleteBirthData(user.birthData)) {
      toast.error('Please provide complete birth details in your profile first');
      return;
    }

    // Check if user's charts are imported
    if (!userStatus || !userStatus.astroAgentsLive || userStatus.astroAgentsLive === 0) {
      toast.error('Please import your astrological charts first');
      return;
    }

    // Check if user has at least one contact with birth data
    const contactsWithBirthData = contacts.filter(contact => 
      contact.birthData && isCompleteBirthData(contact.birthData)
    );
    
    if (contactsWithBirthData.length === 0) {
      toast.error('Please add contacts with birth details to analyze group compatibility');
      return;
    }

    const taggedContacts = extractTaggedContacts(inputMessage);
    
    const userMessage: GroupChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
      taggedContacts
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setShowContactSuggestions(false);
    setIsLoading(true);

    try {
      // Generate or get conversation ID
      let conversationId = currentConversationId;
      if (!conversationId) {
        conversationId = `conv_${Date.now()}`;
        setCurrentConversationId(conversationId);
      }

      // Prepare group members data for the API
      const groupMembers = [
        {
          name: user.name || 'User',
          birthDate: `${user.birthData.year}-${(user.birthData.month || 1).toString().padStart(2, '0')}-${(user.birthData.day || 1).toString().padStart(2, '0')}`,
          birthTime: `${(user.birthData.hour || 0).toString().padStart(2, '0')}:${(user.birthData.minute || 0).toString().padStart(2, '0')}`,
          latitude: user.birthData.latitude || 0,
          longitude: user.birthData.longitude || 0,
          role: 'user'
        }
      ];

      // Add tagged contacts to group members
      if (taggedContacts.length > 0) {
        taggedContacts.forEach(contact => {
          if (contact.birthData) {
            groupMembers.push({
              name: contact.name,
              birthDate: `${contact.birthData.year}-${(contact.birthData.month || 1).toString().padStart(2, '0')}-${(contact.birthData.day || 1).toString().padStart(2, '0')}`,
              birthTime: `${(contact.birthData.hour || 0).toString().padStart(2, '0')}:${(contact.birthData.minute || 0).toString().padStart(2, '0')}`,
              latitude: contact.birthData.latitude || 0,
              longitude: contact.birthData.longitude || 0,
              role: contact.relationship || 'contact'
            });
          }
        });
      }

      // Determine group type and analysis focus from the message
      const groupType = inputMessage.toLowerCase().includes('business') ? 'business' : 
                       inputMessage.toLowerCase().includes('work') ? 'work' : 
                       inputMessage.toLowerCase().includes('family') ? 'family' : 
                       inputMessage.toLowerCase().includes('romance') ? 'romantic' : 
                       inputMessage.toLowerCase().includes('spiritual') ? 'spiritual' : 'friendship';
      
      const analysisFocus = inputMessage.toLowerCase().includes('dynamics') ? 'dynamics' : 
                           inputMessage.toLowerCase().includes('leadership') ? 'leadership' : 
                           inputMessage.toLowerCase().includes('communication') ? 'communication' : 
                           inputMessage.toLowerCase().includes('harmony') ? 'harmony' : 
                           inputMessage.toLowerCase().includes('conflict') ? 'conflict' : 
                           inputMessage.toLowerCase().includes('growth') ? 'growth' : 'dynamics';

      const requestData = {
        groupMembers,
        groupType,
        analysisFocus,
        userId: user.uid
      };
      
      console.log('Sending group compatibility request:', JSON.stringify(requestData, null, 2));

      const response = await fetch('https://rraasibackend--jain-temple-trust.asia-east1.hosted.app/api/compatibility/group-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();
      
      console.log('Response status:', response.status);
      console.log('Response result:', result);

      if (result.success) {
        const assistantMessage: GroupChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.groupAnalysis,
          timestamp: new Date(),
          taggedContacts
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Save both messages to database with conversation ID
        try {
          await chatPersistenceService.saveMessageWithConversationId('user', userMessage.content, conversationId, {
            timestamp: userMessage.timestamp,
            userId: user?.uid,
            taggedContacts: userMessage.taggedContacts?.map(c => ({ id: c.id, name: c.name }))
          });
          
          await chatPersistenceService.saveMessageWithConversationId('assistant', assistantMessage.content, conversationId, {
            timestamp: assistantMessage.timestamp,
            userId: user?.uid,
            taggedContacts: assistantMessage.taggedContacts?.map(c => ({ id: c.id, name: c.name }))
          });
          
          console.log('✅ Messages saved to database with conversation ID:', conversationId);
        } catch (error) {
          console.error('❌ Failed to save messages to database:', error);
          // Fallback to localStorage
          chatPersistenceService.saveToLocalStorage([...messages, userMessage, assistantMessage], user?.uid || '');
        }
        
        // Refresh conversations list if we're in history view
        if (viewMode === 'history') {
          loadConversations();
        }
      } else {
        const errorMessage: GroupChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.error || 'I apologize, but I couldn\'t process your request. Please try again.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        
        // Save error message to database
        try {
          await chatPersistenceService.saveMessageWithConversationId('user', userMessage.content, conversationId, {
            timestamp: userMessage.timestamp,
            userId: user?.uid,
            taggedContacts: userMessage.taggedContacts?.map(c => ({ id: c.id, name: c.name }))
          });
          
          await chatPersistenceService.saveMessageWithConversationId('assistant', errorMessage.content, conversationId, {
            timestamp: errorMessage.timestamp,
            userId: user?.uid
          });
        } catch (error) {
          console.error('Failed to save error messages to database:', error);
        }
      }
    } catch (error) {
      console.error('Error processing chat message:', error);
      
      const errorMessage: GroupChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      
      // Save error message to database
      if (currentConversationId) {
        try {
          await chatPersistenceService.saveMessageWithConversationId('user', userMessage.content, currentConversationId, {
            timestamp: userMessage.timestamp,
            userId: user?.uid,
            taggedContacts: userMessage.taggedContacts?.map(c => ({ id: c.id, name: c.name }))
          });
          
          await chatPersistenceService.saveMessageWithConversationId('assistant', errorMessage.content, currentConversationId, {
            timestamp: errorMessage.timestamp,
            userId: user?.uid
          });
        } catch (error) {
          console.error('Failed to save error messages to database:', error);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const getContactStatusIcon = (contact: Contact) => {
    switch (contact.chartStatus) {
      case 'completed':
        return <Sparkles className="w-4 h-4 text-green-500" />;
      case 'importing':
        return <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
      case 'failed':
        return <div className="w-4 h-4 text-red-500">⚠️</div>;
      default:
        return <div className="w-4 h-4 text-gray-400">⏳</div>;
    }
  };

  // Helper function to check if birth data is complete
  const isCompleteBirthData = (birthData: any) => {
    return birthData && 
           birthData.year && 
           birthData.month && 
           birthData.day && 
           birthData.hour !== undefined && 
           birthData.minute !== undefined && 
           birthData.latitude !== undefined && 
           birthData.longitude !== undefined;
  };

  // Function to help users navigate to chart import
  const handleImportCharts = () => {
    window.location.href = '/astrology';
  };

  const getMessageStyle = (message: GroupChatMessage) => {
    switch (message.type) {
      case 'user':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white ml-auto';
      case 'assistant':
        return 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm text-slate-100 border border-slate-700/50 shadow-xl';
      case 'system':
        return 'bg-blue-500/20 backdrop-blur-sm text-blue-100 border border-blue-500/30';
      default:
        return 'bg-white/10 backdrop-blur-sm text-purple-100';
    }
  };

  const extractSummaryAndDetails = (content: string) => {
    // Look for summary section (usually at the end)
    const summaryMatch = content.match(/(?:In summary|In conclusion|Overall|Summary|Key Points|Bottom Line)[^]*?$/i);
    
    if (summaryMatch) {
      const summary = summaryMatch[0].trim();
      const details = content.replace(summary, '').trim();
      return { summary, details };
    }
    
    // If no clear summary found, take first 2-3 paragraphs as summary
    const paragraphs = content.split(/\n\n/);
    if (paragraphs.length > 4) {
      const summary = paragraphs.slice(0, 2).join('\n\n');
      const details = paragraphs.slice(2).join('\n\n');
      return { summary, details };
    }
    
    // If content is short, show everything
    return { summary: content, details: '' };
  };

  const formatAstrologyResponse = (content: string, isExpanded: boolean = false) => {
    const { summary, details } = extractSummaryAndDetails(content);
    
    return (
      <div className="space-y-4">
        {/* Summary Section */}
        <div className="bg-gradient-to-r from-purple-500/15 to-pink-500/15 rounded-xl p-5 border border-purple-500/30 shadow-lg">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center mr-3">
              <Sparkles className="w-4 h-4 text-purple-300" />
            </div>
            <h4 className="text-base font-bold text-purple-200">Key Insights</h4>
          </div>
          <div className="text-sm leading-relaxed text-slate-200" 
               dangerouslySetInnerHTML={{ __html: summary.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-purple-200">$1</strong>') }} />
        </div>
        
        {/* Detailed Analysis (if expanded) */}
        {isExpanded && details && (
          <div className="mt-4">
            <div className="flex items-center mb-4">
              <div className="w-1 h-1 bg-purple-400 rounded-full mr-2"></div>
              <h4 className="text-sm font-semibold text-purple-200">Detailed Analysis</h4>
            </div>
            {formatDetailedContent(details)}
          </div>
        )}
      </div>
    );
  };

  const formatDetailedContent = (content: string) => {
    // Split content into sections by ### headers
    const sections = content.split(/(###\s+[^\n]+)/g);
    
    return sections.map((section, index) => {
      if (section.startsWith('###')) {
        // This is a header
        const headerText = section.replace(/^###\s+/, '').replace(/\*\*/g, '');
        return (
          <div key={index} className="mb-6">
            <h3 className="text-xl font-bold text-purple-200 mb-4 flex items-center bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg px-4 py-3 border border-purple-500/30">
              <Sparkles className="w-5 h-5 mr-3 text-purple-300" />
              {headerText}
            </h3>
          </div>
        );
      } else if (section.trim()) {
        // This is content
        const paragraphs = section.split(/\n\n/);
        return (
          <div key={index} className="space-y-4">
            {paragraphs.map((paragraph, pIndex) => {
              if (paragraph.trim()) {
                // Check if it's a sub-header (starts with ** and ends with **)
                if (paragraph.match(/^\*\*[^*]+\*\*:/)) {
                  const subHeaderText = paragraph.replace(/^\*\*([^*]+)\*\*:\s*/, '$1:');
                  return (
                    <div key={pIndex} className="mb-4">
                      <h4 className="text-base font-semibold text-purple-200 mb-3 flex items-center bg-purple-500/10 rounded-lg px-3 py-2 border-l-4 border-purple-400">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                        {subHeaderText}
                      </h4>
                    </div>
                  );
                } else {
                  // Handle bullet points and other formatting
                  const formattedParagraph = formatParagraph(paragraph);
                  return (
                    <div key={pIndex} className="mb-4">
                      {formattedParagraph}
                    </div>
                  );
                }
              }
              return null;
            })}
          </div>
        );
      }
      return null;
    });
  };

  const formatParagraph = (paragraph: string) => {
    // Handle bullet points
    if (paragraph.startsWith('- **')) {
      const parts = paragraph.split(':**');
      if (parts.length === 2) {
        const title = parts[0].replace('- **', '');
        const content = parts[1];
        return (
          <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
            <h5 className="text-sm font-semibold text-purple-200 mb-2 flex items-center">
              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-2"></div>
              {title}:
            </h5>
            <p className="text-sm leading-relaxed text-slate-200">
              {content}
            </p>
          </div>
        );
      }
    }
    
    // Handle regular paragraphs with bold text
    const formattedText = paragraph.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-purple-200">$1</strong>');
    
    return (
      <p className="text-sm leading-relaxed text-slate-200" 
         dangerouslySetInnerHTML={{ __html: formattedText }} />
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Star Animation */}
      <StarAnimation starCount={15} />
      
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="bg-purple-800/80 backdrop-blur-sm text-white py-2 px-3 shadow-lg border-b border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {viewMode === 'history' ? (
                <button
                  onClick={() => {
                    setViewMode('chat');
                    if (currentConversationId) {
                      setSearchParams({ conversation: currentConversationId, view: 'chat' });
                    } else {
                      setSearchParams({ view: 'chat' });
                    }
                  }}
                  className="p-1.5 hover:bg-purple-700 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    setViewMode('history');
                    setSearchParams({ view: 'history' });
                    loadConversations();
                  }}
                  className="p-1.5 hover:bg-purple-700 rounded-full transition-colors"
                >
                  <Menu className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => window.history.back()}
                className="p-1.5 hover:bg-purple-700 rounded-full transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold flex items-center truncate">
                  <MessageCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">
                    {viewMode === 'history' ? 'Chat History' : 'Group Compatibility Chat'}
                  </span>
                </h1>
                <p className="text-xs text-purple-200 truncate">
                  {viewMode === 'history' 
                    ? `${conversations.length} conversations` 
                    : currentConversationId 
                      ? `Conversation ${currentConversationId.slice(-8)}` 
                      : 'New Chat'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1 ml-2">
              {viewMode === 'chat' && (
                <button
                  onClick={handleClearChat}
                  className="px-2 py-1 text-xs text-red-400 hover:text-red-300 border border-red-400/30 hover:border-red-300/50 rounded-lg transition-colors"
                  title="Clear this conversation"
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="p-1.5 hover:bg-purple-700 rounded-full transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Help Panel */}
        {showHelp && (
          <div className="bg-blue-500/20 backdrop-blur-sm p-4 border-b border-blue-500/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-blue-100 font-semibold flex items-center">
                <HelpCircle className="w-4 h-4 mr-2" />
                How to Use
              </h3>
              <button
                onClick={() => setShowHelp(false)}
                className="text-blue-200 hover:text-white"
              >
                ×
              </button>
            </div>
            <div className="text-blue-100 text-sm space-y-2">
              <p><strong>@ Mentions:</strong> Type @ followed by a contact name to include them in your analysis</p>
              <p><strong>Examples:</strong></p>
              <ul className="ml-4 space-y-1">
                <li>• "How compatible am I with @Achal for romance?"</li>
                <li>• "Business compatibility with @Priyanshu?"</li>
                <li>• "Friendship dynamics with @Roop"</li>
              </ul>
              <p><strong>Available Contacts:</strong> {contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {viewMode === 'history' ? (
            /* Chat History View */
            <div className="h-full overflow-y-auto p-4">
              {/* New Chat Button */}
              <button
                onClick={handleNewChat}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-2xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center gap-3 mb-6 shadow-lg"
              >
                <Plus className="w-6 h-6" />
                <span className="text-lg font-semibold">Start New Chat</span>
              </button>

              {/* Conversations List */}
              {isLoadingConversations ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-xl font-medium mb-2">No conversations yet</p>
                  <p className="text-sm mb-6">Start your first group compatibility chat to see it here</p>
                  <button
                    onClick={handleNewChat}
                    className="bg-purple-500/20 text-purple-300 px-6 py-3 rounded-xl hover:bg-purple-500/30 transition-all"
                  >
                    Start Chatting
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {conversations.map((conversation) => {
                    if (!conversation.conversationId || !conversation.lastMessageTime) {
                      return null;
                    }
                    
                    return (
                      <div
                        key={conversation.conversationId}
                        onClick={() => handleConversationSelect(conversation.conversationId)}
                        className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-4 cursor-pointer transition-all hover:bg-slate-700/70 border border-slate-700/30 hover:border-slate-600/50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-lg truncate mb-1">
                              {conversation.title}
                            </h3>
                            {conversation.preview && (
                              <p className="text-gray-300 text-sm truncate mb-2">
                                {conversation.preview}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                {conversation.messageCount} messages
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(conversation.lastMessageTime)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversation(conversation.conversationId);
                            }}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all ml-3"
                            title="Delete conversation"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              )}
            </div>
          ) : (
            /* Chat View */
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 chat-messages-container">
                {messages.map(message => {
                  const isExpanded = expandedMessages.has(message.id);
                  const { details } = extractSummaryAndDetails(message.content);
                  const hasDetails = details && details.trim().length > 0;
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl p-4 ${getMessageStyle(message)}`}>
                        {message.type === 'assistant' ? (
                          <div className="space-y-4">
                            <div className="flex items-center mb-3 text-xs text-purple-300">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Astrological Analysis
                            </div>
                            {formatAstrologyResponse(message.content, isExpanded)}
                            
                            {/* Show More/Less Button */}
                            {hasDetails && (
                              <button
                                onClick={() => toggleMessageExpansion(message.id)}
                                className="mt-4 text-xs text-purple-300 hover:text-purple-200 flex items-center transition-colors"
                              >
                                {isExpanded ? (
                                  <>
                                    <span>Show Less</span>
                                    <svg className="w-3 h-3 ml-1 transform rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </>
                                ) : (
                                  <>
                                    <span>Show Detailed Analysis</span>
                                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        ) : message.type === 'user' ? (
                          <div className="text-white">
                            {message.content}
                            {message.taggedContacts && message.taggedContacts.length > 0 && (
                              <div className="mt-2 text-xs text-purple-200">
                                Tagged: {message.taggedContacts.map(c => c.name).join(', ')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-blue-200">
                            <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
                            {/* Action buttons for system messages */}
                            {message.id === 'welcome' && user && (!user.birthData || !isCompleteBirthData(user.birthData)) && (
                              <div className="mt-4 flex gap-2">
                                <button
                                  onClick={() => window.location.href = '/profile'}
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                                >
                                  📝 Complete Profile
                                </button>
                              </div>
                            )}
                            {message.id === 'welcome' && user && user.birthData && isCompleteBirthData(user.birthData) && (!userStatus || !userStatus.astroAgentsLive || userStatus.astroAgentsLive === 0) && (
                              <div className="mt-4 flex gap-2">
                                <button
                                  onClick={handleImportCharts}
                                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                                >
                                  📊 Import Charts
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="text-xs opacity-75 mt-4 flex items-center justify-between border-t border-white/10 pt-2">
                          <span>{message.timestamp.toLocaleTimeString()}</span>
                          {message.taggedContacts && message.taggedContacts.length > 0 && (
                            <span className="flex items-center">
                              <Users className="w-3 h-3 mr-1" />
                              {message.taggedContacts.length} contact{message.taggedContacts.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white/10 backdrop-blur-sm text-purple-100 rounded-2xl p-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        <span className="text-sm ml-2">Analyzing compatibility...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
        </div>

        {/* Contact Suggestions */}
        {showContactSuggestions && (
          <div className="bg-white/10 backdrop-blur-sm p-3 border-t border-white/20">
            <div className="text-xs text-purple-200 mb-2">Press Tab to select, ↑↓ to navigate</div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {contacts
                .filter(contact => 
                  contact.name.toLowerCase().includes(
                    inputMessage.slice(inputMessage.lastIndexOf('@') + 1).split(' ')[0].toLowerCase()
                  )
                )
                .map((contact, index) => (
                  <button
                    key={contact.id}
                    onClick={() => insertContactMention(contact)}
                    className={`w-full text-left p-2 rounded-lg transition-colors ${
                      index === suggestionIndex 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-white/10 text-purple-200 hover:bg-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getContactStatusIcon(contact)}
                        <span className="font-medium">@{contact.name}</span>
                        <span className="text-xs opacity-75">({contact.relationship})</span>
                      </div>
                      <span className="text-xs">Tab</span>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="bg-purple-800/50 backdrop-blur-sm p-4 border-t border-white/20">
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                placeholder="Type @ to mention contacts, e.g., 'How compatible am I with @Achal?'"
                className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-purple-300 rounded-2xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                rows={1}
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
              <div className="absolute right-3 top-3 text-purple-300 text-xs">
                <AtSign className="w-4 h-4" />
              </div>
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-2xl hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
              style={{ minWidth: '48px', minHeight: '48px' }}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          
          {/* Quick Tips */}
          <div className="mt-3 text-xs text-purple-300 flex items-center justify-between">
            <span>💡 Type @ to mention contacts</span>
            <span>{contacts.length} contact{contacts.length !== 1 ? 's' : ''} available</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupCompatibilityChat; 